/**
 * EditConnectionDialog - Dialog for viewing and editing managed connection properties.
 *
 * Uses connector metadata to define form structure (read-only display).
 * Populates values from existing connection's parameterValueSet.
 * Supports OAuth re-authentication for OAuth-based connections.
 */

import { useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  makeStyles,
  Text,
  tokens,
  Spinner,
  Badge,
  shorthands,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  CheckmarkCircle20Filled,
  ErrorCircle20Filled,
  Warning20Filled,
  Key20Regular,
  PlugConnected24Regular,
} from '@fluentui/react-icons';
import { useUpdateConnection, useConnection, useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { useOAuthConnection } from '@/hooks/useOAuthConnection';
import { useEffectiveManifest } from '@/hooks/useEffectiveManifest';
import { useNotificationStore } from '@/stores/notificationStore';
import { getRelativeTime, formatDateTime } from '@/utils/dateTime';
import { getConnectionParameterSets, hasRealParameterSets } from '@/services/manifestConnectionService';
import { hasOAuthParameter } from '@/types/connectionParameters';
import type { ConnectionParameterSet, FormValues } from '@/types/connectionParameters';
import type { AvailableConnection } from '@/types';
import { ConnectionStatus } from '@/types';
import {
  getConnectionStatusLabel,
  getConnectionStatusAppearance,
  getConnectionStatusIconKind,
} from '@/utils/connections';
import { ManifestConnectionForm } from './editForm/ManifestConnectionForm';
import { useManifestFormState } from './editForm/useManifestFormState';
import { DiscardChangesDialog } from './editForm/DiscardChangesDialog';
import { useDiscardGuard } from './editForm/useDiscardGuard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '800px',
    minWidth: '700px',
    minHeight: '500px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  // Header with connector icon
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    marginBottom: tokens.spacingVerticalS,
  },
  headerIcon: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconSymbol: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
  },
  connectorIcon: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    objectFit: 'contain',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '4px',
  },
  headerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  headerSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Section styling
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  sectionHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Info grid for connection details
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalS,
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  infoLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  infoValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },

  // Status display
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },

  // Divider
  divider: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },

  // OAuth re-auth section
  oauthSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  oauthDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  // Loading state
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
  },
  loadingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Footer
  footer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

export interface EditConnectionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** The connection being edited */
  connection: AvailableConnection | null;
  /** Callback after successful save */
  onSaved?: () => void;
}

/**
 * EditConnectionDialog — Dialog for viewing and editing managed connection properties.
 *
 * Uses connector metadata to define form structure (read-only display).
 * Populates values from existing connection's parameterValueSet.
 * Supports OAuth re-authentication for OAuth-based connections.
 */
export const EditConnectionDialog = ({
  open,
  onClose,
  connection,
  onSaved,
}: EditConnectionDialogProps) => {
  const styles = useStyles();
  const updateConnection = useUpdateConnection();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // OAuth re-authentication hook
  const {
    reauthenticateConnection,
    state: oauthState,
    error: oauthError,
    reset: resetOAuth,
  } = useOAuthConnection();

  // Shared manifest-driven form state (displayName + parameter values +
  // selected parameter-set name + dirty flags). See `useManifestFormState`.
  const form = useManifestFormState();
  const {
    displayName,
    setDisplayName,
    formValues: editedFormValues,
    setFormValues: handleFormChange,
    selectedParameterSetName: selectedAuthType,
    setSelectedParameterSetName: handleAuthTypeChange,
    hasFormChanges,
    hasChanges,
    seed,
    seedFormValues,
  } = form;

  // Close/discard orchestration: routes every close affordance (Escape /
  // backdrop / Close / Cancel) through a single dirty check so unsaved edits
  // can't be lost via a path that bypasses it. `resetToOriginal` runs on an
  // explicit discard so the dialog doesn't re-open with stale edits.
  const discardGuard = useDiscardGuard({
    isDirty: hasChanges,
    isBusy: updateConnection.isPending,
    onClose,
    onDiscard: form.resetToOriginal,
  });
  // Stable reference for the seed effect below; the guard object itself
  // re-identifies each render, but `keepEditing` is a stable callback.
  const { keepEditing: resetDiscardConfirm } = discardGuard;

  // Fetch full connection details to get parameterValueSet
  const { data: connectionDetails, isLoading: isLoadingDetails } = useConnection(
    connection?.name ?? '',
    open && !!connection?.name
  );

  // Fetch connector metadata to get available parameter sets (defines form structure)
  const { data: connectorMetadata, isLoading: isLoadingMetadata } = useConnectorMetadata(
    connection?.connectorId
  );

  // Build effective manifest from connector metadata using shared hook
  const effectiveManifest = useEffectiveManifest(connectorMetadata);

  // Bootstrap seed: as soon as the dialog opens with a connection, populate
  // `displayName` from the summary prop and reset chrome. Parameter values
  // still need the details API round-trip below, so we seed empty here.
  useEffect(() => {
    if (open && connection) {
      seed({
        displayName: connection.displayName ?? connection.name ?? '',
        formValues: {},
        selectedParameterSetName: undefined,
      });
      resetDiscardConfirm();
      resetOAuth();
    }
  }, [open, connection, seed, resetOAuth, resetDiscardConfirm]);

  // Details-arrival seed: refreshes ONLY the form values + selected auth
  // type (not displayName), so any in-progress displayName edits made
  // during the loading window aren't clobbered when the API resolves.
  useEffect(() => {
    if (!open) return;
    // Multi-auth format
    if (connectionDetails?.properties?.parameterValueSet?.values) {
      const values = connectionDetails.properties.parameterValueSet.values;
      const initialValues: FormValues = {};
      for (const [key, value] of Object.entries(values)) {
        initialValues[key] = value as FormValues[string];
      }
      seedFormValues(initialValues, connectionDetails.properties.parameterValueSet.name ?? undefined);
    }
    // Legacy single-auth format
    else if (connectionDetails?.properties?.connectionParameters) {
      const values = connectionDetails.properties.connectionParameters;
      const initialValues: FormValues = {};
      for (const [key, value] of Object.entries(values)) {
        initialValues[key] = value as FormValues[string];
      }
      seedFormValues(initialValues, undefined);
    }
  }, [open, connectionDetails, seedFormValues]);

  // Extract connector name from connectorId (last segment)
  const connectorName = useMemo(() => {
    if (!connection?.connectorId) return 'Unknown';
    const parts = connection.connectorId.split('/');
    return parts[parts.length - 1] || 'Unknown';
  }, [connection?.connectorId]);

  // Get parameter sets from manifest - handles both multi-auth and single-auth connectors
  const parameterSets: ConnectionParameterSet[] = useMemo(() => {
    return getConnectionParameterSets(effectiveManifest);
  }, [effectiveManifest]);

  // Whether the connector has real multi-auth parameter sets (vs synthetic single set)
  const isRealMultiAuth = useMemo(
    () => hasRealParameterSets(effectiveManifest),
    [effectiveManifest]
  );

  // Get the effective selected parameter set (user's choice or from connection)
  const selectedParameterSet = useMemo(() => {
    if (selectedAuthType) return selectedAuthType;
    const setName = connectionDetails?.properties?.parameterValueSet?.name;
    if (setName) return setName;
    return parameterSets[0]?.name;
  }, [selectedAuthType, connectionDetails, parameterSets]);

  // Get current parameter set based on connection's selection
  const currentParameterSet = useMemo(() => {
    if (!selectedParameterSet && parameterSets.length > 0) {
      return parameterSets[0];
    }
    return parameterSets.find((s) => s.name === selectedParameterSet) ?? parameterSets[0];
  }, [parameterSets, selectedParameterSet]);

  // Check if the current parameter set is OAuth-based
  const isOAuthConnection = useMemo(() => {
    if (!currentParameterSet) return false;
    return hasOAuthParameter(currentParameterSet);
  }, [currentParameterSet]);

  // OAuth re-authentication is in progress
  const isReauthenticating = oauthState === 'authenticating' || oauthState === 'confirming' || oauthState === 'syncing';

  // OAuth re-authentication handler using shared hook
  const handleReauthenticate = useCallback(async () => {
    if (!connection?.name) return;

    const success = await reauthenticateConnection({
      connectionName: connection.name,
    });

    if (success) {
      addNotification({
        type: 'success',
        title: 'Re-authenticated successfully',
        message: `Connection "${connection.displayName ?? connection.name}" has been re-authenticated.`,
      });
      onSaved?.();
    } else if (oauthError) {
      addNotification({
        type: 'error',
        title: 'Re-authentication failed',
        message: oauthError,
      });
    }
  }, [connection, reauthenticateConnection, addNotification, onSaved, oauthError]);

  // Format timestamps
  const createdTimeFormatted = useMemo(() => {
    if (!connection?.createdTime) return null;
    const date = new Date(connection.createdTime);
    return {
      relative: getRelativeTime(date, { style: 'phrase' }),
      absolute: formatDateTime(date, 'locale'),
    };
  }, [connection?.createdTime]);

  const modifiedTimeFormatted = useMemo(() => {
    if (!connection?.lastModifiedTime) return null;
    const date = new Date(connection.lastModifiedTime);
    return {
      relative: getRelativeTime(date, { style: 'phrase' }),
      absolute: formatDateTime(date, 'locale'),
    };
  }, [connection?.lastModifiedTime]);

  // Get status display info
  const statusInfo = useMemo(() => {
    if (!connection) return { label: 'Unknown', appearance: 'outline' as const, icon: null };
    const iconKind = getConnectionStatusIconKind(connection.status);
    const icon =
      iconKind === 'success' ? (
        <CheckmarkCircle20Filled className={styles.statusConnected} />
      ) : iconKind === 'error' ? (
        <ErrorCircle20Filled className={styles.statusError} />
      ) : iconKind === 'warning' ? (
        <Warning20Filled className={styles.statusWarning} />
      ) : null;
    return {
      label: getConnectionStatusLabel(connection.status),
      appearance: 'filled' as const,
      icon,
    };
  }, [connection, styles.statusConnected, styles.statusError, styles.statusWarning]);

  const handleSave = async () => {
    if (!connection) return;

    const trimmedDisplayName = displayName.trim();

    try {
      // Build the params object with display name, parameter values, and auth type
      const params: {
        connectionId: string;
        displayName?: string;
        parameterValues?: Record<string, unknown>;
        parameterSetName?: string;
      } = {
        connectionId: connection.id,
      };

      // Add display name if changed
      if (trimmedDisplayName && trimmedDisplayName !== (connection.displayName ?? connection.name)) {
        params.displayName = trimmedDisplayName;
      }

      // Add parameter set name if there is selected auth type
      if (selectedAuthType) {
        params.parameterSetName = selectedAuthType;
        // When changing auth type, always send the form values
        params.parameterValues = editedFormValues as Record<string, unknown>;
      } else if (hasFormChanges) {
        // Only updating values without changing auth type
        params.parameterValues = editedFormValues as Record<string, unknown>;
      }

      await updateConnection.mutateAsync({
        connectionName: connection.name,
        params,
      });
      addNotification({
        type: 'success',
        title: 'Connection updated',
        message: `Connection "${trimmedDisplayName || connection.name}" has been updated successfully.`,
      });
      onSaved?.();
      onClose();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Failed to update connection',
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    }
  };

  const isLoading = isLoadingDetails || isLoadingMetadata;

  // Get status badge color
  const statusColor = useMemo(
    () => getConnectionStatusAppearance(connection?.status),
    [connection?.status],
  );

  // Render the header icon
  const renderHeaderIcon = () => {
    if (connectorMetadata?.iconUri) {
      return (
        <img
          src={connectorMetadata.iconUri}
          alt=""
          className={styles.connectorIcon}
          aria-hidden="true"
        />
      );
    }
    return (
      <div className={styles.headerIcon}>
        <PlugConnected24Regular className={styles.headerIconSymbol} />
      </div>
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={discardGuard.handleOpenChange}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={discardGuard.requestClose}
              />
            }
          >
            Edit connection
          </DialogTitle>
          <DialogContent className={styles.content}>
            {/* Header with connector icon and status */}
            <div className={styles.header}>
              {renderHeaderIcon()}
              <div className={styles.headerInfo}>
                <Text className={styles.headerTitle}>
                  {connection?.displayName ?? connection?.name ?? 'Connection'}
                </Text>
                <Text className={styles.headerSubtitle}>
                  {connectorMetadata?.displayName ?? connectorName}
                </Text>
              </div>
              <Badge appearance={statusInfo.appearance} color={statusColor}>
                {statusInfo.label}
              </Badge>
            </div>

            {/* Display name + Authentication (shared with SP edit dialog) */}
            <ManifestConnectionForm
              displayName={displayName}
              onDisplayNameChange={setDisplayName}
              displayNamePlaceholder={connection?.name ?? 'Enter display name'}
              displayNameTestId="edit-connection-display-name"
              parameterSets={parameterSets}
              selectedParameterSet={selectedParameterSet ?? parameterSets[0]?.name ?? ''}
              onParameterSetChange={handleAuthTypeChange}
              values={editedFormValues}
              onValuesChange={handleFormChange}
              showAuthTypeSelector={isRealMultiAuth && parameterSets.length > 1}
              disabled={updateConnection.isPending}
              isLoading={isLoading}
              oauthSlot={
                /* OAuth re-authentication button — managed-only affordance
                    injected into the shared form's auth section. */
                isOAuthConnection && parameterSets.length > 0 && currentParameterSet ? (
                  <div className={styles.oauthSection}>
                    <Text className={styles.oauthDescription}>
                      {connection?.status === ConnectionStatus.Unauthenticated
                        ? 'This connection needs to be re-authenticated.'
                        : 'Sign in again to refresh the authentication token.'}
                    </Text>
                    <Button
                      appearance="primary"
                      icon={isReauthenticating ? <Spinner size="tiny" /> : <Key20Regular />}
                      onClick={handleReauthenticate}
                      disabled={isReauthenticating}
                    >
                      {isReauthenticating ? 'Signing in...' : 'Sign in'}
                    </Button>
                  </div>
                ) : null
              }
            />

            <Divider className={styles.divider} />

            {/* Properties Section */}
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Properties</Text>
              <div className={styles.infoGrid}>
                {createdTimeFormatted && (
                  <div className={styles.infoItem}>
                    <Text className={styles.infoLabel}>Created</Text>
                    <Text className={styles.infoValue} title={createdTimeFormatted.absolute}>
                      {createdTimeFormatted.relative}
                    </Text>
                  </div>
                )}
                {modifiedTimeFormatted && (
                  <div className={styles.infoItem}>
                    <Text className={styles.infoLabel}>Last modified</Text>
                    <Text className={styles.infoValue} title={modifiedTimeFormatted.absolute}>
                      {modifiedTimeFormatted.relative}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions className={styles.footer}>
            <Button appearance="secondary" onClick={discardGuard.requestClose} disabled={updateConnection.isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={!hasChanges || updateConnection.isPending || (!hasChanges && isOAuthConnection)}
              {...(updateConnection.isPending ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {updateConnection.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* Discard-confirmation dialog — shared across every ConnectionList
        edit surface for consistent copy + affordances. Rendered as a sibling
        of the main dialog so its open state is independent. */}
    <DiscardChangesDialog
      open={discardGuard.showDiscardConfirm}
      onKeepEditing={discardGuard.keepEditing}
      onDiscard={discardGuard.confirmDiscard}
    />
    </>
  );
};

EditConnectionDialog.displayName = 'EditConnectionDialog';
